"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import Image from "next/image";
import { Breadcrumb } from "../breadcrumb";
import { Heading1 } from "../typography";
import { ButtonLink } from "../button-link";
import { BannerBlob, BannerStats, type BannerStat } from "../shared";

const bannerStats: BannerStat[] = [
  { value: "20 Years", label: "Environmental Trends", color: "blue" },
  { value: "40%", label: "Increase in Building Footprint", color: "green" },
  { value: "15", label: "Policy Recommendations", color: "orange" },
];

export const Banner = () => {
  useSectionNavTheme({
    trigger: "#act-banner",
    isDark: false,
  });

  useGSAP(() => {
    const tl = gsap.timeline();

    const splitTitle = new SplitText(".banner-text", {
      type: "words,chars",
    });

    tl.from(splitTitle.chars, {
      opacity: 0,
      y: 20,
      duration: 0.8,
      stagger: 0.02,
      ease: "power2.out",
    })
      .from(
        ".act-subtitle",
        {
          opacity: 0,
          y: 30,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.4"
      )
      .from(
        ".act-description",
        {
          opacity: 0,
          y: 30,
          duration: 0.6,
          ease: "power2.out",
        },
        "-=0.3"
      )
      .from(
        ".breadcrumb",
        {
          opacity: 0,
          y: 20,
          duration: 0.5,
          ease: "power2.out",
        },
        "-=0.3"
      );
  });

  return (
    <section
      id="act-banner"
      className="relative py-20 lg:py-32 bg-haizea-dark min-h-screen flex flex-col overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <BannerBlob className="z-0 opacity-30" />
        <Image
          src="/images/aerial-view-of-australian-suburb.webp"
          alt="" // Decorative background
          fill
          className="absolute z-10 inset-0 object-cover w-full h-full opacity-30 dark:opacity-20"
          style={{ objectPosition: "center" }}
          priority
          fetchPriority="high"
          draggable={false}
          data-speed="0.5"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-haizea-green/70 via-haizea-dark/20 to-haizea-blue/40 z-20"></div>
      </div>

      <div className="container relative mb-16 z-30">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Case Studies", href: "/case-studies" },
            { label: "ACT Environmental Assessment" },
          ]}
          className="mb-8 absolute left-0 top-0"
        />
      </div>

      <div className="container relative z-30 flex-1 flex items-center">
        <div className="max-w-6xl mx-auto text-center">
          <Heading1 className="banner-text text-white mb-8">
            Evidence-based environmental policy in the ACT
          </Heading1>
          {/* Stats Grid */}
          <BannerStats
            stats={bannerStats}
            className="mt-8 lg:mt-12 max-w-4xl justify-center mx-auto"
          />

          <div
            id="banner-buttons"
            className="flex items-center justify-center gap-4 md:gap-8 mt-8 lg:mt-12 lg:gap-10 flex-wrap"
          >
            <ButtonLink
              backgroundColorClass="bg-haizea-green"
              href={"https://act.terrak.io/"}
              target="_blank"
            >
              Visit the ACT Data Explorer
            </ButtonLink>
          </div>
        </div>
      </div>
    </section>
  );
};
